import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { TextField, MenuItem, IconButton, Menu } from '@mui/material'
import styled from '@emotion/styled'
import { Activity, Client, GroupByOption } from '../../../pure-js/types/GrooverTypes'
import { getActivitiesByCustomerId, getClient } from '../libs/DBApiHandler'
import { getGrooverText } from '../libs/GrooverTextRepository'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import { Texts } from '../../../pure-js/libs/Texts'
import { ActivityCard, ActivityCardListView, ActivityCardCompact } from '../components/activities/ActivityCard'
import { filterByDanceStyle, groupAndSortActivities } from '../utils/activityUtils'
import { readPublicActivitiesInitialData } from '../libs/readPublicActivitiesInitialData'
import {
  mergePublicActivitiesSearchParams,
  publicActivitiesSearchParamsFromState
} from '../libs/publicActivitiesSearchParams'
import { filterActivePublicActivities } from '../../../pure-js/libs/PublicActivitiesHelper'
import { setupIframeHeightSync, isIframeEmbedDebugEnabled, IframeHeightSyncConfig } from '../utils/iframeHeightSync'
import Box from '../components/Box'
import { GROUP_BY_OPTIONS, CONTAINER_MAX_WIDTH } from '../config/activity.config'
import {
  setThemeConfig,
  setResolvedThemeConfig,
  getThemeConfig,
  DARK_MODE_PRESETS,
  DarkModePreset,
  resolvePublicActivitiesThemeConfig,
  resolveActivityHoverBackground,
  ThemeConfig
} from '../themeConfig'
import GridViewIcon from '@mui/icons-material/GridView'
import ViewListIcon from '@mui/icons-material/ViewList'
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek'
import ViewCompactAltIcon from '@mui/icons-material/ViewCompactAlt'
import { ActivityCardDesignType } from '../components/activities/ActivityCardDesignType'
import ActivityWeekView from '../components/activities/ActivityWeekView'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Tooltip from '@mui/material/Tooltip'
import { STYLE_COLORS } from '../components/activities/ViewHelper'
import JsonModal from '../components/JsonModal'
import { JsonIcon } from '../components/Icons'
import { useClientRouteHost } from '../context/ClientRouteHostContext'

// Utility function for mobile detection
const isMobile = () => window.innerWidth <= 768

// Check if we're in development/localhost mode
const isDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

export const PublicActivitiesPage: React.FC = () => {
  const { clientId } = useClientRouteHost()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Extract filter values from the URL params
  const searchText = searchParams.get('filter') ?? ''
  const searchGroup = (searchParams.get('group') as GroupByOption) ?? 'style'
  const cityName = searchParams.get('cityName') ?? ''
  const danceStyle = searchParams.get('danceStyle') ?? ''
  const activityType = searchParams.get('activityType') ?? ''
  const groupSorting = searchParams.get('groupSorting') ?? 'startdate'
  const viewModeParam = searchParams.get('viewMode') ?? 'grid'
  const hideFilter = searchParams.get('hideFilter') === 'true'
  const period = searchParams.get('period') ?? ''
  const periodId = searchParams.get('periodId') ?? ''
  const showSubEvents = searchParams.get('showSubEvents') === 'true'
  const showParentEvent = searchParams.get('showParentEvent') !== 'false' // Default to true
  const disableMonthGrouping = searchParams.get('disableMonthGrouping') === 'true'
  const fieldsParam = searchParams.get('fields')
  const fields = fieldsParam != null ? fieldsParam.split(',').filter(Boolean) : undefined
  const locationId = searchParams.get('locationId') ?? ''
  const instructor = searchParams.get('instructor') ?? ''
  const excludeDropinOnly = searchParams.get('excludeDropinOnly') === 'true'
  const showDraftActivities = searchParams.get('showDraftActivities') === 'true'
  const darkModePresetUrlParam = searchParams.get('darkModePreset') ?? ''
  const rawMaxWidth = searchParams.get('maxWidth') ?? ''
  // Normalize bare numbers to px so embeds using e.g. data-max-width="1400" work correctly
  const normalizedMaxWidth = /^\d+(\.\d+)?$/.test(rawMaxWidth.trim()) ? `${rawMaxWidth.trim()}px` : rawMaxWidth.trim()
  const maxWidth = /^\d+(\.\d+)?(px|%|em|rem|vw|vh)$|^auto$/.test(normalizedMaxWidth)
    ? normalizedMaxWidth
    : CONTAINER_MAX_WIDTH

  const applyDarkModePresetOverride = (base: ThemeConfig, client: Client | null): ThemeConfig => {
    if (!darkModePresetUrlParam || !DARK_MODE_PRESETS[darkModePresetUrlParam as DarkModePreset]) return base
    const overrideClient = {
      ...(client ?? {}),
      themeConfig: {
        ...(client?.themeConfig ?? {}),
        darkModePreset: darkModePresetUrlParam
      }
    } as Client
    return resolvePublicActivitiesThemeConfig(overrideClient)
  }

  // State Variables
  const initialSsrData = useMemo(() => readPublicActivitiesInitialData(), [])
  const [activities, setActivities] = useState<Activity[]>(() =>
    filterActivePublicActivities(initialSsrData?.activities ?? [], { includeDrafts: showDraftActivities })
  )
  const [filterText, setFilterText] = useState(searchText)
  // Production: group/viewMode come from embed URL only. Localhost: editable via UI below.
  const [groupBy, setGroupBy] = useState<GroupByOption>(searchGroup)
  const [viewModeState, setViewModeState] = useState<'grid' | 'high-grid' | 'week' | 'list' | 'compact'>(
    viewModeParam as 'grid' | 'high-grid' | 'week' | 'list' | 'compact'
  )
  const [client, setClient] = useState<Client | null>(initialSsrData?.client ?? null)
  const [isLoading, setIsLoading] = useState(!initialSsrData)
  const [isMobileView, setIsMobileView] = useState(isMobile())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [jsonModalOpen, setJsonModalOpen] = useState(false)
  const [jsonModalData, setJsonModalData] = useState<unknown>(null)

  // Restore persisted config on remount (SPA back-navigation never triggers
  // iframe.onload, so the parent won't re-send IFRAME_CONFIG). window.__IFRAME_CONFIG
  // is written by both PublicActivitiesPage and ActivityDetailContent, so it
  // survives the route change and gives us the correct iframeId immediately.
  const iframeConfigRef = useRef<IframeHeightSyncConfig>(
    ((window as unknown as Record<string, unknown>).__IFRAME_CONFIG as IframeHeightSyncConfig | undefined) ?? {
      isMultipleIframes: false,
      forceNewTab: false,
      iframeId: null,
      debug: false
    }
  )

  const [currentTheme, setCurrentTheme] = useState(() => {
    if (initialSsrData?.client) {
      setThemeConfig(initialSsrData.client)
    }
    const resolved = applyDarkModePresetOverride(getThemeConfig(), initialSsrData?.client ?? null)
    // Keep module store in sync so cards/hover (getThemeConfig) match page chrome
    setResolvedThemeConfig(resolved)
    return resolved
  })

  const updateUrlSearchParams = (updates: Record<string, string | boolean | undefined>) => {
    setSearchParams(mergePublicActivitiesSearchParams(searchParams, updates))
  }

  const currentSearchParamState = () =>
    publicActivitiesSearchParamsFromState({
      filterText,
      groupBy,
      cityName,
      danceStyle,
      activityType,
      groupSorting,
      viewMode: viewModeState,
      hideFilter,
      period,
      periodId,
      showSubEvents,
      showParentEvent,
      disableMonthGrouping,
      lang: searchParams.get('lang')
    })

  useEffect(() => {
    if (initialSsrData?.client) {
      document.body.style.backgroundColor = currentTheme.backgroundColor
      document.documentElement.style.backgroundColor = currentTheme.backgroundColor
    }
  }, [initialSsrData, currentTheme.backgroundColor])

  // Re-resolve when URL darkModePreset changes (iframe configurator live preview)
  useEffect(() => {
    if (!client && !initialSsrData?.client) return
    const baseClient = client ?? initialSsrData?.client ?? null
    if (!baseClient) return
    setThemeConfig(baseClient)
    const resolved = applyDarkModePresetOverride(getThemeConfig(), baseClient)
    setResolvedThemeConfig(resolved)
    setCurrentTheme(resolved)
    document.body.style.backgroundColor = resolved.backgroundColor
    document.documentElement.style.backgroundColor = resolved.backgroundColor
    // applyDarkModePresetOverride closes over darkModePresetUrlParam
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: react to URL preset + client identity
  }, [darkModePresetUrlParam, client, initialSsrData?.client])

  useEffect(() => {
    if (client?.name) {
      document.title = client.name
    }
  }, [client?.name])

  useEffect(() => {
    if (initialSsrData) return undefined

    let isMounted = true

    const fetchData = async () => {
      setIsLoading(true)
      const [activitiesResult, clientResult] = await Promise.all([
        getActivitiesByCustomerId(clientId || ''),
        getClient(clientId || '')
      ])

      if (!isMounted) return

      setActivities(filterActivePublicActivities(activitiesResult, { includeDrafts: showDraftActivities }))
      if (clientResult) {
        setClient(clientResult)
        setThemeConfig(clientResult)
        const updatedTheme = applyDarkModePresetOverride(getThemeConfig(), clientResult)
        setResolvedThemeConfig(updatedTheme)
        setCurrentTheme(updatedTheme)

        document.body.style.backgroundColor = updatedTheme.backgroundColor
        document.documentElement.style.backgroundColor = updatedTheme.backgroundColor
      }
      setIsLoading(false)
    }
    fetchData()

    return () => {
      isMounted = false
      document.body.style.backgroundColor = ''
      document.documentElement.style.backgroundColor = ''
    }
  }, [clientId, initialSsrData, showDraftActivities])

  useEffect(() => {
    if (!isDev) return undefined
    const handleResize = () => {
      setIsMobileView(isMobile())
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Listen for iframe configuration from parent
  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (!event.data) return
      const { type, isMultipleIframes, forceNewTab, iframeId } = event.data

      if (type === 'IFRAME_CONFIG') {
        // Guard: never overwrite a valid iframeId with null/undefined
        const current = iframeConfigRef.current
        const prevIframeId = current.iframeId
        iframeConfigRef.current = {
          isMultipleIframes: isMultipleIframes ?? current.isMultipleIframes,
          forceNewTab: forceNewTab ?? current.forceNewTab,
          iframeId: iframeId ?? current.iframeId,
          debug: event.data.debug ?? current.debug
        }
        // Persist config on window so it survives route changes (e.g. activity detail)
        ;(window as unknown as Record<string, unknown>).__IFRAME_CONFIG = { ...iframeConfigRef.current }
        const nextIframeId = iframeConfigRef.current.iframeId
        if (isIframeEmbedDebugEnabled() && nextIframeId && nextIframeId !== prevIframeId) {
          console.log(
            `📥 Received iframe config: multiple=${isMultipleIframes}, forceNewTab=${forceNewTab}, iframeId=${nextIframeId}`
          )
        }
      }
    }

    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [])

  // Apply search filters and grouping
  const filteredActivities = useMemo(() => {
    let filtered = activities

    // Only filter out subevents if showSubEvents is false
    if (!showSubEvents) {
      filtered = filtered.filter((activity) => !activity.subeventParentId)
    }

    // Only show parent events if showParentEvent is true
    if (!showParentEvent) {
      // Hide parent events (activities with eventType = 'Parentevent')
      filtered = filtered.filter((activity) => activity.eventType !== 'Parentevent')
    }

    if (filterText) {
      filtered = filtered.filter((activity) => activity.name.toLowerCase().includes(filterText.toLowerCase()))
    }

    if (danceStyle) {
      filtered = filterByDanceStyle(filtered, danceStyle)
    }

    if (activityType) {
      filtered = filtered.filter((activity) => activity.type === activityType)
    }

    // Support both periodId (new) and period string (legacy)
    if (periodId) {
      // Prioritize periodId if available
      filtered = filtered.filter((activity) => activity.periodId === periodId)
    } else if (period) {
      // Fallback to legacy period string
      filtered = filtered.filter((activity) => activity.period === period)
    }

    if (locationId) {
      filtered = filtered.filter((activity) => activity.locationId === locationId)
    }

    if (instructor) {
      const q = instructor.toLowerCase()
      filtered = filtered.filter((activity) => activity.instructorsNames?.toLowerCase().includes(q))
    }

    if (excludeDropinOnly) {
      filtered = filtered.filter((activity) => !activity.dropinOnly)
    }

    if (cityName) {
      filtered = filtered.filter((a) => a.city?.toLowerCase() === cityName.toLowerCase())
    }

    return filtered
  }, [
    activities,
    filterText,
    danceStyle,
    activityType,
    period,
    periodId,
    locationId,
    instructor,
    showSubEvents,
    showParentEvent,
    excludeDropinOnly,
    cityName
  ])

  const groupedActivities = useMemo(() => {
    return groupAndSortActivities(
      filteredActivities,
      groupBy,
      groupSorting,
      cityName,
      client as Client,
      disableMonthGrouping
    )
  }, [filteredActivities, groupBy, groupSorting, cityName, client, disableMonthGrouping])

  const hasListingContent = useMemo(() => {
    return Object.values(groupedActivities).some((subGroups) =>
      Object.values(subGroups).some((activitiesInGroup) => activitiesInGroup.length > 0)
    )
  }, [groupedActivities])

  // Handle filter text change
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFilterText(value)
    updateUrlSearchParams({ ...currentSearchParamState(), filter: value || undefined })
  }

  const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value as GroupByOption
    setGroupBy(value)
    updateUrlSearchParams({ ...currentSearchParamState(), group: value })
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewModeChange = (mode: 'grid' | 'high-grid' | 'week' | 'list' | 'compact') => {
    setViewModeState(mode)
    updateUrlSearchParams({ ...currentSearchParamState(), viewMode: mode })
    handleMenuClose()
  }

  // Enhanced activity click handler
  const handleActivityClick = (activityId: string) => {
    const lang = searchParams.get('lang')
    const langQuery = lang ? { lang } : undefined

    const activityObj = activities.find((a) => a.id === activityId)
    const clientSlug = client?.slug
    const activitySlug = activityObj?.seoLink

    const path =
      clientSlug && activitySlug
        ? buildRoute(RoutePath.ACTIVITY_DETAIL_SEO, { clientSlug, activitySlug }, langQuery)
        : buildRoute(RoutePath.ACTIVITY_DETAIL, { activityId }, langQuery)

    // Determine if we should open in new tab
    const shouldOpenInNewTab = iframeConfigRef.current.forceNewTab || client?.openActivityInNewTab

    if (shouldOpenInNewTab) {
      window.open(path, '_blank')
      return
    }

    return navigate(path, {
      state: { from: 'PublicActivitiesPage' }
    })
  }

  const renderSection = (groupKey: string, subGroups: { [key: string]: Activity[] }, showGroupLabel: boolean) => {
    const subGroupEntries = Object.entries(subGroups)
    const showSubGroupLabel = subGroupEntries.length > 1
    const shouldShowGroupLabel = showGroupLabel && groupBy !== 'none' && groupKey !== ''
    const shouldShowSubGroupLabel =
      groupBy !== 'none' && groupBy !== groupSorting && showSubGroupLabel && subGroupEntries[0]?.[0] !== ''
    return (
      <Section key={`section-${groupKey}`} fullWidth $viewMode={viewModeState}>
        {shouldShowGroupLabel && (
          <GroupLabel $textColor={currentTheme.textColor || '#222'} $compact={viewModeState === 'compact'}>
            {groupKey}
          </GroupLabel>
        )}
        {subGroupEntries.map(([subGroupKey, activities], subIdx) => (
          <div
            key={`sub-section-${subGroupKey}`}
            style={viewModeState === 'compact' && subIdx > 0 ? { marginTop: '20px' } : undefined}
          >
            {shouldShowSubGroupLabel && (
              <GroupLabel
                $textColor={currentTheme.textColor || '#222'}
                $compact={viewModeState === 'compact'}
                $subGroup
              >
                {subGroupKey}
              </GroupLabel>
            )}
            <ActivityGrid
              viewMode={viewModeState}
              styleColor={
                activities[0]?.style
                  ? client?.themeConfig?.danceStyles?.find(
                      (ds) => ds.name.toLowerCase() === activities[0].style?.toLowerCase()
                    )?.color ||
                    STYLE_COLORS[activities[0].style.toLowerCase()] ||
                    STYLE_COLORS.default
                  : STYLE_COLORS.default
              }
            >
              {activities.map((activity) => {
                if (viewModeState === 'high-grid') {
                  return (
                    <ActivityCardDesignType
                      key={activity.id}
                      activity={activity}
                      client={client}
                      onClick={() => handleActivityClick(activity.id)}
                    />
                  )
                }
                if (viewModeState === 'list') {
                  return (
                    <ActivityCardListView
                      key={activity.id}
                      activity={activity}
                      client={client}
                      onClick={() => handleActivityClick(activity.id)}
                      fields={fields}
                    />
                  )
                }
                if (viewModeState === 'compact') {
                  return (
                    <ActivityCardCompact
                      key={activity.id}
                      activity={activity}
                      client={client}
                      onClick={() => handleActivityClick(activity.id)}
                      fields={fields}
                    />
                  )
                }
                return (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    client={client}
                    onClick={() => handleActivityClick(activity.id)}
                  />
                )
              })}
            </ActivityGrid>
          </div>
        ))}
      </Section>
    )
  }

  // Keeps the embedded iframe sized to fit content — runs identically for single-
  // and multi-iframe embeds (see setupIframeHeightSync for why continuous tracking
  // matters for both). Re-reads iframeConfigRef.current on every send, so it stays
  // correct even if IFRAME_CONFIG (with iframeId) arrives after this effect starts.
  useEffect(() => {
    return setupIframeHeightSync(() => iframeConfigRef.current)
  }, [activities])
  return (
    <PageWrapper $backgroundColor={currentTheme.backgroundColor}>
      <Container
        fullWidth
        hideFilter={hideFilter}
        data-id="public-activity-page"
        $backgroundColor={currentTheme.backgroundColor}
        $maxWidth={maxWidth}
      >
        {(!hideFilter || isDev) && (
          <FilterContainer direction="row" fullWidth mobileColumn>
            {!hideFilter && (
              <StyledTextField
                label={getGrooverText(Texts.adminGeneralSearchHelperText)}
                value={filterText}
                onChange={handleFilterChange}
                fullWidth
                $isDark={
                  currentTheme.backgroundColor === '#1a1a1a' ||
                  currentTheme.backgroundColor === '#2d2d2d' ||
                  currentTheme.backgroundColor === '#000000' ||
                  currentTheme.backgroundColor === '#1a1a2e'
                }
                $textColor={currentTheme.textColor || '#222'}
              />
            )}
            {isDev && (
              <StyledTextField
                select
                label={getGrooverText(Texts.adminGeneralInputFieldsGroupByLabel)}
                value={groupBy}
                onChange={handleGroupChange}
                $isDark={
                  currentTheme.backgroundColor === '#1a1a1a' ||
                  currentTheme.backgroundColor === '#2d2d2d' ||
                  currentTheme.backgroundColor === '#000000' ||
                  currentTheme.backgroundColor === '#1a1a2e'
                }
                $textColor={currentTheme.textColor || '#222'}
              >
                {GROUP_BY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {getGrooverText(Texts[option.label])}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
            {isDev && (
              <ViewToggleContainer>
                <Tooltip title="Show Activities JSON" arrow enterDelay={300}>
                  <IconButton
                    onClick={() => {
                      setJsonModalData({
                        activities: filteredActivities,
                        client: client,
                        filters: {
                          filterText,
                          groupBy,
                          cityName,
                          danceStyle,
                          activityType,
                          groupSorting,
                          period,
                          periodId,
                          showSubEvents,
                          showParentEvent
                        }
                      })
                      setJsonModalOpen(true)
                    }}
                    sx={{
                      color: '#666',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <JsonIcon size={20} color="currentColor" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleMenuClick}
                  color={
                    viewModeState === 'grid' ||
                    viewModeState === 'high-grid' ||
                    viewModeState === 'week' ||
                    viewModeState === 'list' ||
                    viewModeState === 'compact'
                      ? 'primary'
                      : 'default'
                  }
                >
                  {viewModeState === 'grid' ? (
                    <GridViewIcon />
                  ) : viewModeState === 'high-grid' ? (
                    <CalendarViewWeekIcon />
                  ) : viewModeState === 'list' ? (
                    <ViewListIcon />
                  ) : viewModeState === 'compact' ? (
                    <ViewCompactAltIcon />
                  ) : (
                    <GridViewIcon />
                  )}
                  <ArrowDropDownIcon sx={{ marginLeft: '-4px' }} />
                </IconButton>
                {!isMobileView && (
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={() => handleViewModeChange('grid')}>
                      <GridViewIcon style={{ marginRight: 8 }} />
                      Grid View
                    </MenuItem>
                    <MenuItem onClick={() => handleViewModeChange('high-grid')}>
                      <CalendarViewWeekIcon style={{ marginRight: 8 }} />
                      High Grid View
                    </MenuItem>
                    <MenuItem onClick={() => handleViewModeChange('list')}>
                      <ViewListIcon style={{ marginRight: 8 }} />
                      List View
                    </MenuItem>
                    <MenuItem onClick={() => handleViewModeChange('compact')}>
                      <ViewCompactAltIcon style={{ marginRight: 8 }} />
                      Compact View
                    </MenuItem>
                    <MenuItem onClick={() => handleViewModeChange('week')}>
                      <CalendarViewWeekIcon style={{ marginRight: 8 }} />
                      Week View
                    </MenuItem>
                  </Menu>
                )}
              </ViewToggleContainer>
            )}
          </FilterContainer>
        )}
        <ActivitiesContainer fullWidth>
          {isLoading ? (
            <LoadingSkeleton $textColor={currentTheme.textColor || '#222'} />
          ) : !hasListingContent ? (
            <EmptyListingMessage $textColor={currentTheme.textColor || '#222'}>
              No activities found.
            </EmptyListingMessage>
          ) : viewModeState === 'week' ? (
            <ActivityWeekView
              activities={filteredActivities}
              client={client ?? undefined}
              fields={fields ?? undefined}
              groupBy={groupBy}
            />
          ) : (
            Object.entries(groupedActivities).map(([groupKey, subGroups], idx, arr) =>
              renderSection(groupKey, subGroups, arr.length > 1)
            )
          )}
        </ActivitiesContainer>

        {/* JSON Modal */}
        <JsonModal
          open={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          data={jsonModalData}
          title="Public Activities Data"
        />
      </Container>
    </PageWrapper>
  )
}

// Styled Components
const PageWrapper = styled.div<{ $backgroundColor: string }>`
  width: 100%;
  min-height: 100vh;
  background: ${(props) => props.$backgroundColor};
`

interface ContainerProps {
  hideFilter?: boolean
}
const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$backgroundColor' && prop !== '$maxWidth' && prop !== 'hideFilter'
})<ContainerProps & { $backgroundColor: string; $maxWidth: string }>`
  max-width: ${(props) => props.$maxWidth};
  margin: 0 auto;
  padding: 20px;
  padding-top: ${(props) => (props.hideFilter ? '60px' : '20px')};
  background: ${(props) => props.$backgroundColor};
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 10px;
    padding-top: ${(props) => (props.hideFilter ? '40px' : '10px')};
  }
`
const FilterContainer = styled(Box)`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`
const ViewToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== '$isDark' && prop !== '$textColor'
})<{ $isDark: boolean; $textColor: string }>`
  background: ${(props) => (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')};
  border-radius: 4px;
  min-width: 200px;

  & .MuiInputBase-input {
    color: ${(props) => props.$textColor || '#222'};
  }

  & .MuiInputLabel-root {
    color: ${(props) => props.$textColor || 'rgba(0, 0, 0, 0.6)'};
  }
`
const ActivitiesContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`
const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$viewMode'
})<{ $viewMode?: string }>`
  margin-bottom: ${(props) => (props.$viewMode === 'compact' ? '40px' : '24px')};
  font-size: 16px;
`
const GroupLabel = styled.div<{ $textColor: string; $compact?: boolean; $subGroup?: boolean }>`
  color: ${(props) => (props.$subGroup ? `${props.$textColor}99` : props.$textColor)};
  font-size: ${(props) => (props.$subGroup ? '0.85em' : '1em')};
  font-style: ${(props) => (props.$subGroup ? 'italic' : 'normal')};
  margin-bottom: ${(props) => (props.$compact ? '6px' : '16px')};

  & span {
    color: ${(props) => (props.$subGroup ? `${props.$textColor}99` : props.$textColor)} !important;
  }
`
const EmptyListingMessage = styled.p<{ $textColor: string }>`
  margin: 0;
  opacity: 0.7;
  color: ${(props) => props.$textColor};
`

const LoadingSkeleton = styled.div<{ $textColor: string }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  min-height: 320px;

  &::before,
  &::after {
    content: '';
    display: block;
    min-height: 180px;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      ${(props) => props.$textColor}11 25%,
      ${(props) => props.$textColor}22 50%,
      ${(props) => props.$textColor}11 75%
    );
    background-size: 200% 100%;
    animation: pa-skeleton-shimmer 1.2s ease-in-out infinite;
  }

  @keyframes pa-skeleton-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`
interface ActivityGridProps {
  viewMode: 'grid' | 'high-grid' | 'week' | 'list' | 'compact'
  styleColor?: string
}
const ActivityGrid = styled.div<ActivityGridProps>`
  display: flex;
  flex-direction: ${(props) =>
    props.viewMode === 'grid' || props.viewMode === 'high-grid' || props.viewMode === 'week' ? 'row' : 'column'};
  flex-wrap: ${(props) =>
    props.viewMode === 'grid' || props.viewMode === 'high-grid'
      ? 'wrap'
      : props.viewMode === 'week'
        ? 'nowrap'
        : 'nowrap'};
  gap: ${(props) => (props.viewMode === 'compact' ? '0px' : '8px')};

  & > * {
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    /* List view uses outline-only hover on ListViewCard — don't replace card background */
    ${(props) =>
      props.viewMode === 'list'
        ? ''
        : `
      &:hover {
        background: ${resolveActivityHoverBackground(getThemeConfig())};
      }
    `}
  }

  @media (min-width: 769px) {
    ${(props) =>
      props.viewMode === 'grid' &&
      `
      & > * {
        flex: 0 0 calc(25% - 6px);
        max-width: calc(25% - 6px);
      }
    `}
    ${(props) =>
      props.viewMode === 'high-grid' &&
      `
      & > * {
        flex: 0 0 calc(25% - 6px);
        max-width: calc(25% - 6px);
        min-height: 400px;
        margin-bottom: 20px;
      }
    `}
  }

  ${(props) =>
    props.viewMode === 'list' &&
    `
    & > * {
      flex: 1 1 100%;
      max-width: 100%;
      min-width: 0;
    }
  `}

  ${(props) =>
    props.viewMode === 'compact' &&
    `
    & > * {
      flex: 1 1 100%;
      max-width: 100%;
      min-width: 0;
    }
  `}

  @media (max-width: 768px) {
    ${(props) =>
      (props.viewMode === 'grid' || props.viewMode === 'high-grid') &&
      `
        flex-direction: column;
        flex-wrap: nowrap;
        & > * {
          flex: 1 1 100%;
          max-width: 100%;
          min-width: 0;
        }
      `}
  }
`
